import * as React from 'react';
import { Group, Box, Collapse, ThemeIcon, Text, UnstyledButton, createStyles, rem } from '@mantine/core';
import { IconCalendarStats, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { NavLink } from 'react-router-dom';

// Types
import { NavItem } from '../../types';

// Local Components
import { NavLinkItem } from '../NavLinkItem';
import { NavLinkItemWrapper } from '../NavLinkItemWrapper';

const useStyles = createStyles((theme) => ({
    control: {
        fontWeight: 500,
        display: 'block',
        width: '100%',
        padding: `${theme.spacing.xs} ${theme.spacing.md}`,
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
        fontSize: theme.fontSizes.sm,

        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
            color: theme.colorScheme === 'dark' ? theme.white : theme.black
        }
    },

    link: {
        fontWeight: 500,
        display: 'block',
        textDecoration: 'none',
        padding: `${theme.spacing.xs} ${theme.spacing.md}`,
        paddingLeft: rem(31),
        marginLeft: rem(30),
        fontSize: theme.fontSizes.sm,
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
        borderLeft: `${rem(1)} solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,

        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
            color: theme.colorScheme === 'dark' ? theme.white : theme.black
        }
    },

    chevron: {
        transition: 'transform 200ms ease'
    }
}));

interface LinksGroupProps extends NavItem {}

export function LinksGroup({
    icon: Icon,
    label,
    initiallyOpened,
    links,
    link,
    end,
    notificationCount
}: LinksGroupProps) {
    const { classes, theme } = useStyles();
    const hasLinks = Array.isArray(links);
    const [opened, setOpened] = React.useState(initiallyOpened || false);
    const ChevronIcon = theme.dir === 'ltr' ? IconChevronRight : IconChevronLeft;
    const childItems = (hasLinks ? links : []).map((link, index) => (
        <NavLinkItem className={classes.link} {...link} key={index} />
    ));

    return hasLinks ? (
        <>
            <NavLinkItemWrapper onClick={() => setOpened((o) => !o)} isWrapped>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {Icon ? (
                        <ThemeIcon variant="subtle" size={30}>
                            <Icon size="1.1rem" />
                        </ThemeIcon>
                    ) : null}
                    <Box ml="md">{label}</Box>
                </Box>
                <ChevronIcon
                    className={classes.chevron}
                    size="1rem"
                    stroke={1.5}
                    style={{
                        transform: opened ? `rotate(${theme.dir === 'rtl' ? -90 : 90}deg)` : 'none'
                    }}
                />
            </NavLinkItemWrapper>
            <Collapse in={opened}>{childItems}</Collapse>
        </>
    ) : (
        <NavLinkItem
            icon={Icon}
            label={label}
            link={link ?? ''}
            notificationCount={notificationCount}
            end={end}
            wrapped
        />
    );
}
